import { Badge } from 'common/src/designSystem/components/badge';
import { Flex } from 'common/src/designSystem/components/flex';
import { Link } from 'common/src/designSystem/components/link';
import { Skeleton } from 'common/src/designSystem/components/skeleton';
import { Cell } from 'common/src/designSystem/components/table/cell';
import {
    DelegationId,
    UpdateCellDelegationsQuery,
    VolunteerRegistrationFragment
} from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import { DelegationsPaths } from 'common/src/util/paths/delegationsPaths';
import * as React from 'react';
import { UpdateCellDropdown } from '../../../components/cells/updateCellDropdown';
import { RichSelect } from '../../../designSystem/components/richSelect/richSelect';
import { useFiltersColumnsContext } from '../../../filtersColumns/filtersColumnsContext';
import {
    executeUpdateCellDelegationsQuery,
    useUsersInfosMassDelegationDeleteMutation,
    useVolunteersRegistrationsMassDelegationAddMutation
} from '../../../generated/graphqlHooks';
import { useHeavent } from '../../../hooks/useHeavent';
import { useSegmentsContext } from '../../../segments/segmentsContext';
import { getToken } from '../../../util/aws/cognito';

interface IVolunteerRegistrationDelegationProps {
    updatable: boolean;
    volunteerRegistration: VolunteerRegistrationFragment;

    reload(): void;
}

export const VolunteerRegistrationDelegation = (props: IVolunteerRegistrationDelegationProps) => {
    const {
        translate,
        params: { organizationId, eventId }
    } = useHeavent();
    const { mutate: massDelegationAdd } = useVolunteersRegistrationsMassDelegationAddMutation();
    const { mutate: massDelegationDelete } = useUsersInfosMassDelegationDeleteMutation();
    // TODO: temporary solution
    // TODO: use only useFiltersColumnsContext() once refacto is done
    const { isEditMode: segmentsIsEditMode } = useSegmentsContext();
    const { isEditMode: richTableIsEditMode } = useFiltersColumnsContext();
    const isEditMode = segmentsIsEditMode || richTableIsEditMode;
    const delegation = React.useMemo(
        () => props.volunteerRegistration.delegation,
        [props.volunteerRegistration.delegation]
    );
    const initialValue = React.useMemo(() => (delegation ? [delegation.id] : []), [delegation]);
    const [isFirstTime, setIsFirstTime] = React.useState(true);
    const [allDelegations, setAllDelegations] = React.useState<
        UpdateCellDelegationsQuery['event']['delegations']['nodes']
    >([]);
    const onStateChange = React.useCallback(
        async (isOpen) => {
            if (isOpen && isFirstTime) {
                const {
                    event: { delegations }
                } = await executeUpdateCellDelegationsQuery({ eventId }, await getToken());

                setAllDelegations(delegations.nodes);
                setIsFirstTime(false);
            }
        },
        [isFirstTime]
    );
    const onSave = React.useCallback(
        async (value: DelegationId[]) => {
            if (isNonEmptyArray(value)) {
                await massDelegationAdd({
                    eventId,
                    delegationId: value[0],
                    selecteds: { ids: [props.volunteerRegistration.userInfo.id] }
                });
            } else if (delegation) {
                await massDelegationDelete({
                    organizationId,
                    eventId,
                    delegationId: delegation.id,
                    selecteds: { ids: [props.volunteerRegistration.userInfo.id] },
                    deleteFromEvent: false
                });
            }

            props.reload();
        },
        [eventId, props.volunteerRegistration]
    );

    if (isEditMode && props.updatable) {
        return (
            <UpdateCellDropdown
                css={{ gap: '$2' }}
                initialValue={initialValue}
                renderInput={(value, setValue) =>
                    isFirstTime ? (
                        <Flex direction="column" gap="1" width={1}>
                            <Skeleton borderRadius="$1" height={20} width={1} />
                            <Skeleton borderRadius="$1" height={40} width={1} />
                        </Flex>
                    ) : (
                        <RichSelect
                            isSearchVisible={true}
                            label={translate('Delegation')}
                            renderOnPortal={true}
                            values={value}
                            onChange={setValue}
                        >
                            {allDelegations.map((delegation) => (
                                <option key={delegation.id} value={delegation.id}>
                                    {delegation.name}
                                </option>
                            ))}
                        </RichSelect>
                    )
                }
                renderValue={() =>
                    delegation ? (
                        <Badge cursor="pointer" ellipsis={true}>
                            {delegation.name}
                        </Badge>
                    ) : null
                }
                onSave={onSave}
                onStateChange={onStateChange}
            />
        );
    } else {
        return (
            <Cell css={{ border: '2px solid transparent' }}>
                {delegation && (
                    <Badge cursor="pointer" ellipsis={true} title={delegation.name}>
                        <Link
                            to={DelegationsPaths.DELEGATION({
                                organizationId,
                                eventId,
                                delegationId: delegation.id
                            })}
                        >
                            {delegation.name}
                        </Link>
                    </Badge>
                )}
            </Cell>
        );
    }
};
